import { useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router-dom'
import { dispatchSignals } from '@prelude.so/js-sdk/signals/slim'
import { useGet, usePatch, usePost } from 'api'
import { FormikValues } from 'formik'
import { load } from 'recaptcha-v3'

import { RequestMethod } from 'meta/api/requestMethod'
import { SmsProvider } from 'meta/pages/v4/otp'
import { Routes } from 'meta/routes'
import { ApiEndpoint } from 'controller/endpoint'
import { RECAPTCHA_ACTION, RECAPTCHA_HOST, RECAPTCHA_TOKEN } from 'utils/constants/auth'
import { CookieClient, CookieName } from 'utils/cookie'

import { useGoogleRecaptchaKey, usePreludeSdkKey, useS2SToken } from 'hooks/useEnv'
import { useOnUpdate } from 'hooks/useOnUpdate'

import { IFormOnSubmit, IFormProps } from '../types'

const hookRequestMap = {
  [RequestMethod.get]: useGet,
  [RequestMethod.patch]: usePatch,
  [RequestMethod.post]: usePost,
}

export const useOnSubmit = (
  props: IFormProps
): { onSubmit: IFormOnSubmit; error?: Response | Error | string; response?: object } => {
  const { action, useProxy, usePreludeSdk, authToken, bodyParser, onSuccess, recaptcha, requestMethod, setFormStatus } =
    props

  const googleRecaptchaKey = useGoogleRecaptchaKey()
  const preludeSdkKey = usePreludeSdkKey()
  const TsmsS2SToken = useS2SToken()
  // const router = useRouter()
  //@ts-expect-error requestMethod might not be defined
  const { fetch, fetching, error, response, status, exceptions } = hookRequestMap[requestMethod]()

  // this is needed in order to fetch sms provider info separately from the main form submission
  const { fetch: fetchSms, error: errorSms, response: responseSms, status: statusSms } = useGet()

  const bodyRef = useRef<object>(null)
  const recaptchaRef = useRef(null)

  const navigate = useNavigate()

  const [smsServiceProvider, setSmsServiceProvider] = useState<SmsProvider | null>(null)
  const [fieldValues, setFieldValues] = useState<FormikValues | null>(null)

  const onSubmit: IFormOnSubmit = async (fieldValues: FormikValues): Promise<void> => {
    setFieldValues(fieldValues)

    if (usePreludeSdk && fieldValues.phonePrefix) {
      // qui bisogna fare la chiamata a /v4/otp/flow per capire quale provider usare
      await fetchSms({
        urlResource: ApiEndpoint.v4.otp.flow(fieldValues.phonePrefix),
        useV4: true,
      })
    } else {
      setSmsServiceProvider(SmsProvider.NONE)
    }
  }

  useEffect(() => {
    const submitData = async () => {
      let sendFieldValues = { ...fieldValues }

      if (smsServiceProvider === SmsProvider.PRELUDE && usePreludeSdk) {
        const dispatchId = await dispatchSignals(preludeSdkKey)

        sendFieldValues = {
          ...sendFieldValues,
          dispatchId,
        }
      }

      //@ts-expect-error bodyRef might not be defined
      bodyRef.current = bodyParser ? bodyParser(sendFieldValues) : sendFieldValues

      if (action && recaptcha) {
        //@ts-expect-error recaptchaRef might be null
        const token = await recaptchaRef.current.execute(recaptcha.action)
        //@ts-expect-error bodyRef might not be defined
        bodyRef.current = {
          ...bodyRef.current,
          [RECAPTCHA_TOKEN]: token,
          [RECAPTCHA_ACTION]: recaptcha.action,
          [RECAPTCHA_HOST]: `${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`,
        }
      }
      if (action) {
        await fetch({
          authToken,
          body: bodyRef.current,
          urlResource: action,
          useProxy: useProxy ? useProxy : null,
          extraHeaders: {
            'Tsms-S2S-Token': TsmsS2SToken,
          },
        })
      } else {
        onSuccess(bodyRef.current, {})
      }
    }

    if (smsServiceProvider && fieldValues) {
      submitData()
    }
  }, [smsServiceProvider, fieldValues])

  useEffect(() => {
    if (recaptcha && googleRecaptchaKey) {
      ;(async () => {
        //@ts-expect-error recaptchaRef might be null
        recaptchaRef.current = await load(googleRecaptchaKey, {
          useEnterprise: true,
        })
        //@ts-expect-error recaptchaRef might be null
        if (isMobile) recaptchaRef.current.hideBadge()
      })()
    }
  }, [googleRecaptchaKey, recaptcha])

  useOnUpdate(() => {
    if (response) {
      onSuccess(bodyRef.current as FormikValues, response)
    }
  }, [response])

  useOnUpdate(() => {
    if (authToken && status === 401) {
      CookieClient.remove(CookieName.TOKEN)
      // router.push(AppRoute.Profile.default)
    }

    if (status === 403 && action == ApiEndpoint.purchase.form()) {
      if (exceptions && !exceptions[0].includes('DUPLICATE_FIELDS_NOT_PERMITTED')) {
        navigate(authToken ? Routes.ProfileTransactions.generatePath() : Routes.Success.generatePath())
      }
    }
  }, [status])

  useOnUpdate(() => {
    if (setFormStatus) {
      setFormStatus({ fetching, error, response, status })
    }
  }, [fetching, error, response, status])

  useOnUpdate(() => {
    if (errorSms || statusSms === 404) {
      setSmsServiceProvider(SmsProvider.SMS_HOSTING)
    }

    if (responseSms) {
      setSmsServiceProvider(responseSms.data?.provider)
    }
  }, [errorSms, responseSms, statusSms])

  return { onSubmit, error, response }
}
